import { useEffect, useState } from 'react';
import type { PagePayload } from '../types/pagePayload';
import type { TemplateComponent } from '../types/template';
import { loadTemplate } from './TemplateRuntime';
import ErrorPage from '../components/ErrorPage';
import LoadingPage from '../components/LoadingPage';
import { getPublishedForm, submitPublishedForm, subscribeToEmailUpdates } from '../api/forms';
import { isolateCollectionPayload } from './collectionPayload';

interface TemplateLoaderProps {
  payload: PagePayload;
}

export default function TemplateLoader({ payload }: TemplateLoaderProps) {
  const [Template, setTemplate] = useState<TemplateComponent | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;

    setTemplate(null);
    setError(null);

    loadTemplate(payload.template)
      .then((component) => {
        if (!cancelled) setTemplate(() => component);
      })
      .catch((templateError) => {
        if (!cancelled) setError(templateError);
      });

    return () => {
      cancelled = true;
    };
  }, [
    payload.template.id,
    payload.template.version,
    payload.template.entrypoint,
    payload.template.stylesheet,
  ]);

  if (error) {
    return <ErrorPage message="Unable to load the page template." />;
  }

  if (!Template) {
    return <LoadingPage />;
  }

  const isolated = isolateCollectionPayload(payload);

  return (
    <Template
      forms={{ get: getPublishedForm, submit: submitPublishedForm, subscribe: subscribeToEmailUpdates }}
      page={isolated.page}
      content={isolated.content}
      globalContent={isolated.globalContent}
      collections={isolated.collections}
      assetBase={payload.template.entrypoint.substring(0, payload.template.entrypoint.lastIndexOf("/") + 1)}
    />
  );
}
